import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const ItSupport = () => {
  const { user } = useAuth();
  const [supports, setSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortType, setSortType] = useState('latest');
  const [sortByOpen, setSortByOpen] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchSupports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        status: statusFilter,
        sortType: sortType,
        sortByOpen: sortByOpen
      };
      
      const response = await apiService.post('itSupport', params);
      if (response && response.data) {
        setSupports(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load IT support requests');
      console.error('IT Support fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, statusFilter, sortType, sortByOpen]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSupports();
    }, keyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchSupports]);

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      const response = await apiService.get(`deleteItSupport/${deleteId}`);
      if (response.status === true) {
        setSupports(supports.filter(support => support._id !== deleteId));
        setShowDeleteModal(false);
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to delete IT support request');
      console.error('Delete error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await apiService.post(`itSupportStatus/${id}`, { status: newStatus });
      if (response.status) {
        setSupports(supports.map(s => s._id === id ? { ...s, status: newStatus } : s));
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Status update error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;

  const hasPermission = (module, subModule, action) => {
    if (user?.role === 'admin') return true;
    return false;
  };

  return (
    <div className="p-0">
      <nav className="text-sm mb-6">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">IT Support</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setCurrentPage(1); }}
                placeholder="Search query here.."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-[200px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="All">Show All</option>
              <option value="1">Open (Pending/In Progress)</option>
              <option value="0">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={sortType}
              onChange={(e) => { setSortType(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="latest">Latest First</option>
              <option value="oldest">Oldest First</option>
            </select>
            <select
              value={sortByOpen}
              onChange={(e) => { setSortByOpen(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Default Sort</option>
              <option value="1">Open First</option>
              <option value="0">Completed First</option>
            </select>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : supports.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-headset text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No IT Support Requests</h3>
              <p className="text-gray-500">No IT support requests found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-4 py-3">Index</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Registered User</th>
                    <th className="px-4 py-3">Message</th>
                    <th className="px-4 py-3">Date</th>
                    {hasPermission('itsupport', 'itsupport', 'edit') && (
                      <>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Action</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {supports.map((support, index) => (
                    <tr key={support._id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-3 text-sm">{startIndex + index + 1}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{support.name}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{support.email}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{support.phone}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{support.type || 'travellinglark'}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        {support.userId && support.userId.first_name ? (
                          <div>
                            <div>{support.userId.first_name} {support.userId.last_name || ''}</div>
                            <small className="text-gray-500">{support.userId.email || 'N/A'}</small>
                            <br />
                            <small className="text-gray-500">{support.userId.phone || 'N/A'}</small>
                          </div>
                        ) : (
                          'Guest User'
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{support.message}</td>
                      <td className="px-2 sm:px-4 py-3 text-sm">{formatDate(support.createdAt)}</td>
                      {hasPermission('itsupport', 'itsupport', 'edit') && (
                        <>
                          <td className="px-4 py-3">
                            <select
                              value={support.status}
                              onChange={(e) => handleStatusChange(support._id, e.target.value)}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleDelete(support._id)}
                              className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200"
                            >
                              Delete
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {supports.length > 0 && (
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(startIndex + recordsPerPage, totalRecords)}</span> of{' '}
                <span className="font-medium">{totalRecords}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  const pageNum = startPage + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-2 sm:px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-[#009ef7]/10 border-[#009ef7] text-[#009ef7]'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this IT Support request?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItSupport;



